import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "@/lib/api";
import { formatDate, hasCapability } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Wrench, CalendarClock, TrendingUp, AlertTriangle, Sparkles, LayoutGrid, Trash2,
} from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function intensityClasses(count, maxCount) {
  if (count === 0) return "bg-slate-100 text-slate-300 hover:bg-slate-200";
  const ratio = maxCount > 0 ? count / maxCount : 0;
  if (ratio >= 0.8) return "bg-[#7C1D3A] text-white hover:bg-[#6c1c36] shadow-sm";
  if (ratio >= 0.5) return "bg-[#B8355A] text-white hover:bg-[#7C1D3A]";
  if (ratio >= 0.25) return "bg-[#E07897] text-white hover:bg-[#B8355A]";
  return "bg-[#F5C4D3] text-[#7C1D3A] hover:bg-[#E07897]";
}

export default function Maintenance() {
  const { user } = useAuth();
  const canPurge = hasCapability(user, "manage_company_settings");
  const [upcoming, setUpcoming] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [months, setMonths] = useState("12");
  const [loading, setLoading] = useState(true);

  // Purge stale preventive PMs (admin-only cleanup)
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeBefore, setPurgeBefore] = useState(() => {
    const d = new Date(); d.setDate(1); // sensible default: 1st of current month
    return d.toISOString().slice(0, 10);
  });
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);

  const runPurge = async (dryRun) => {
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-preventive-pms", {
        before_date: purgeBefore,
        dry_run: dryRun,
      });
      if (dryRun) {
        setPurgePreview(r.data);
      } else {
        toast.success(`Purged ${r.data.deleted} scheduled preventive PMs (${r.data.affected_assets} assets touched)`);
        setPurgeOpen(false);
        setPurgePreview(null);
        // Reload data so the heatmap reflects the cleanup.
        const [u, h] = await Promise.all([
          api.get("/maintenance/upcoming"),
          api.get(`/maintenance/heatmap?months=${months}`),
        ]);
        setUpcoming(u.data); setHeatmap(h.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [u, h] = await Promise.all([
        api.get("/maintenance/upcoming"),
        api.get(`/maintenance/heatmap?months=${months}`),
      ]);
      setUpcoming(u.data);
      setHeatmap(h.data);
      setLoading(false);
    })();
  }, [months]);

  // Group weeks by month for the heatmap grid
  const grouped = useMemo(() => {
    if (!heatmap) return [];
    const map = {};
    heatmap.weeks.forEach((w) => {
      const key = `${w.year}-${String(w.month_num).padStart(2, "0")}`;
      if (!map[key]) {
        map[key] = {
          key, label: `${MONTH_NAMES[w.month_num - 1]} ${w.year}`,
          weeks: [], total: 0,
        };
      }
      map[key].weeks.push(w);
      map[key].total += w.total;
    });
    return Object.values(map);
  }, [heatmap]);

  const busiestWeeks = useMemo(() => {
    if (!heatmap) return [];
    return [...heatmap.weeks]
      .filter((w) => w.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [heatmap]);

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Schedule</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Maintenance Schedule</h1>
          <p className="text-slate-500 mt-1 text-sm">Workload heatmap & upcoming preventive / breakdown maintenance</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canPurge && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPurgePreview(null); setPurgeOpen(true); }}
              className="text-red-700 border-red-200 hover:bg-red-50"
              data-testid="purge-pms-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Purge stale PMs
            </Button>
          )}
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[180px]" data-testid="heatmap-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Next 3 months</SelectItem>
              <SelectItem value="6">Next 6 months</SelectItem>
              <SelectItem value="12">Next 12 months</SelectItem>
              <SelectItem value="24">Next 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Purge dialog */}
      <Dialog open={purgeOpen} onOpenChange={(v) => { setPurgeOpen(v); if (!v) setPurgePreview(null); }}>
        <DialogContent data-testid="purge-pms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-4 h-4" /> Purge stale preventive PMs
            </DialogTitle>
            <DialogDescription>
              Deletes every preventive maintenance <b>scheduled</b> before the cutoff date. Completed history is
              preserved. The auto-scheduler will create fresh next PMs on its next tick. This action is logged in
              <code className="font-mono mx-1">admin_cleanup_audit</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">Cutoff date (delete PMs scheduled BEFORE this)</Label>
              <Input
                type="date"
                value={purgeBefore}
                onChange={(e) => { setPurgeBefore(e.target.value); setPurgePreview(null); }}
                data-testid="purge-cutoff-date"
              />
            </div>
            {purgePreview && (
              <div className={`text-xs rounded-md p-3 ${purgePreview.would_delete > 0 ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-50 border border-slate-200 text-slate-600"}`}>
                <div className="font-semibold mb-1">
                  Dry-run for {purgePreview.before_date}
                </div>
                Would delete <b>{purgePreview.would_delete}</b> scheduled preventive PM{purgePreview.would_delete === 1 ? "" : "s"} across <b>{purgePreview.affected_assets}</b> asset{purgePreview.affected_assets === 1 ? "" : "s"}.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)} data-testid="purge-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => runPurge(true)}
              disabled={!purgeBefore || purging}
              data-testid="purge-dry-run"
            >
              Preview count
            </Button>
            <Button
              onClick={() => {
                if (!window.confirm(`Delete ${purgePreview?.would_delete ?? "the matching"} PMs? This cannot be undone from the UI.`)) return;
                runPurge(false);
              }}
              disabled={!purgeBefore || purging || !purgePreview || purgePreview.would_delete === 0}
              className="bg-red-700 hover:bg-red-800"
              data-testid="purge-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {purging ? "Purging…" : `Delete ${purgePreview?.would_delete ?? ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary KPIs */}
      {heatmap && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Tasks"
            value={heatmap.total_tasks}
            sub={`Next ${months} months`}
            icon={LayoutGrid}
            testid="kpi-heatmap-total"
          />
          <SummaryCard
            title="Preventive"
            value={heatmap.preventive}
            sub="Scheduled + logged"
            icon={Wrench}
            testid="kpi-heatmap-preventive"
          />
          <SummaryCard
            title="Breakdowns"
            value={heatmap.breakdown}
            sub="Unscheduled"
            icon={AlertTriangle}
            testid="kpi-heatmap-breakdown"
            tone={heatmap.breakdown > 0 ? "warning" : "default"}
          />
          <SummaryCard
            title="Peak Week"
            value={heatmap.peak_week?.count || 0}
            sub={heatmap.peak_week?.week_start ? formatDate(heatmap.peak_week.week_start) : "—"}
            icon={TrendingUp}
            testid="kpi-heatmap-peak"
            tone={(heatmap.peak_week?.count || 0) >= 5 ? "warning" : "default"}
          />
        </div>
      )}

      {/* Heatmap */}
      <Card data-testid="workload-heatmap">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-[#7C1D3A]" /> Workload Heatmap
          </CardTitle>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <span>Less</span>
            <div className="w-3 h-3 rounded-sm bg-slate-100" />
            <div className="w-3 h-3 rounded-sm bg-[#F5C4D3]" />
            <div className="w-3 h-3 rounded-sm bg-[#E07897]" />
            <div className="w-3 h-3 rounded-sm bg-[#B8355A]" />
            <div className="w-3 h-3 rounded-sm bg-[#7C1D3A]" />
            <span>More</span>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loading || !heatmap ? (
            <div className="text-center text-slate-400 py-8">Loading heatmap...</div>
          ) : (
            <div className="space-y-2">
              {grouped.map((month) => (
                <div key={month.key} className="flex items-center gap-3" data-testid={`heatmap-row-${month.key}`}>
                  <div className="w-24 shrink-0 text-xs font-mono text-slate-600 flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{month.label}</span>
                    <span className="text-slate-400">{month.total || ""}</span>
                  </div>
                  <div className="flex gap-1.5 flex-1 flex-wrap">
                    {month.weeks.map((w) => (
                      <WeekCell key={w.week_start} week={w} maxCount={heatmap.max_count} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Busiest weeks */}
      {busiestWeeks.length > 0 && (
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#7C1D3A]" /> Busiest Weeks Ahead
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-slate-100" data-testid="busiest-weeks">
            {busiestWeeks.map((w) => (
              <div key={w.week_start} className="px-6 py-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Week of {formatDate(w.week_start)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {w.preventive} preventive · {w.breakdown} breakdown ·
                    {" "}{w.scheduled} scheduled · {w.completed} completed
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${w.total >= 5 ? "bg-red-100 text-red-700 border-red-200" : "bg-[#fcf0f3] text-[#7C1D3A] border-[#f5c4d3]"} border`}>
                    {w.total} task{w.total !== 1 ? "s" : ""}
                  </Badge>
                  {w.total >= 5 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-xs">
                      Bottleneck
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upcoming maintenance list */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#7C1D3A]" /> Upcoming Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : upcoming.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              <Wrench className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No upcoming maintenance scheduled.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {upcoming.map((log) => (
                <NavLink
                  key={log.id}
                  to={`/assets/${log.asset_id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-slate-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#fcf0f3] flex items-center justify-center shrink-0">
                      <Wrench className="w-4 h-4 text-[#7C1D3A]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        {log.asset_name}
                        {log.auto_generated && (
                          <Badge variant="outline" className="border-slate-300 text-slate-500 text-[10px] px-1.5 py-0">
                            <Sparkles className="w-2.5 h-2.5 mr-0.5" /> Auto
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">{log.asset_code} · {log.description}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 font-mono text-xs">
                      {formatDate(log.date)}
                    </Badge>
                    <div className="text-xs text-slate-400 mt-1 capitalize">{log.type}</div>
                  </div>
                </NavLink>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WeekCell({ week, maxCount }) {
  const cls = intensityClasses(week.total, maxCount);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`w-9 h-9 rounded-md text-xs font-semibold flex items-center justify-center transition-colors cursor-pointer ${cls}`}
          data-testid={`heatmap-week-${week.week_start}`}
          aria-label={`Week of ${week.week_start}: ${week.total} tasks`}
        >
          {week.total || ""}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" side="top" align="center">
        <div className="p-3 border-b border-slate-100">
          <div className="text-xs font-mono text-slate-500">
            Week of {formatDate(week.week_start)} — {formatDate(week.week_end)}
          </div>
          <div className="font-heading text-lg font-bold text-slate-900 mt-1">
            {week.total} task{week.total !== 1 ? "s" : ""}
          </div>
          <div className="flex gap-2 mt-1 text-xs">
            <span className="text-green-700">{week.preventive} preventive</span>
            {week.breakdown > 0 && <span className="text-red-700">{week.breakdown} breakdown</span>}
          </div>
        </div>
        {week.assets.length === 0 ? (
          <div className="p-4 text-xs text-slate-400 text-center">No assets scheduled</div>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {week.assets.map((a, i) => (
              <NavLink
                key={`${a.id}-${i}`}
                to={`/assets/${a.id}`}
                className="block px-3 py-2 hover:bg-slate-50 text-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-slate-500">{a.asset_id}</span>
                  <Badge variant="outline" className="capitalize text-[10px] px-1.5 py-0">{a.type}</Badge>
                </div>
                <div className="text-slate-900 font-medium mt-0.5 truncate">{a.name}</div>
                <div className="text-slate-400 text-[10px]">{a.category} · {formatDate(a.date)}</div>
              </NavLink>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SummaryCard({ title, value, sub, icon: Icon, tone = "default", testid }) {
  const toneCls = {
    default: "bg-white",
    warning: "bg-amber-50 border-amber-200",
  }[tone];
  return (
    <Card className={`${toneCls} card-hover`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">{title}</p>
            <p className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</p>
            {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
          </div>
          <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-[#7C1D3A]" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
